import { useEffect, useState } from "react";
import axios from "axios";
import { MapPinned } from "lucide-react";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pctFmt = (v) => `${v}%`;
const ROWS = [
  { key: "medianHouseholdIncome", label: "Median Household Income", fmt: (v) => `$${Math.round(v).toLocaleString()}` },
  { key: "medianAge", label: "Median Age", fmt: (v) => v.toFixed(1) },
  { key: "bachelorsOrHigherPct", label: "Bachelor's Degree Or Higher", fmt: pctFmt },
  { key: "age65PlusPct", label: "Age 65 And Over", fmt: pctFmt },
  { key: "homeownerPct", label: "Homeowners", fmt: pctFmt },
  { key: "foreignBornPct", label: "Foreign Born", fmt: pctFmt },
  { key: "veteranPct", label: "Veterans", fmt: pctFmt },
  { key: "unemploymentPct", label: "Unemployment", fmt: pctFmt },
  { key: "whitePct", label: "White", fmt: pctFmt },
  { key: "blackPct", label: "Black", fmt: pctFmt },
  { key: "hispanicPct", label: "Hispanic Or Latino", fmt: pctFmt },
  { key: "asianPct", label: "Asian", fmt: pctFmt },
];

const useConstituency = (id) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    if (!id) return undefined;
    axios.get(`${API}/legislators/${id}/constituency`).then((r) => alive && setD(r.data)).catch(() => alive && setD({ available: false }));
    return () => { alive = false; };
  }, [id]);
  return d;
};

export const ConstituencyCompare = ({ legislators }) => {
  const [a, b] = legislators;
  const da = useConstituency(a?.id);
  const db = useConstituency(b?.id);
  if (!a || !b) return null;
  const loading = da === null || db === null;
  const national = da?.national || db?.national;
  const short = (leg) => (leg.name || "").split(",")[0];
  const cell = (val, other, fmt) => {
    if (val == null) return <td className="p-3 text-right font-mono text-gray-400">n/a</td>;
    const lead = other != null && val > other;
    return (
      <td className={`p-3 text-right font-mono tabular-nums ${lead ? "font-bold text-gray-900 bg-orange-50" : "text-gray-700"}`}>{fmt(val)}</td>
    );
  };
  return (
    <Card className="p-6 mb-8 bg-white rounded-xl border-2 border-gray-200" data-testid="constituency-compare-card">
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-1">
        <MapPinned className="w-5 h-5 text-orange-500" /> Constituency Snapshot
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Who each member answers to: district figures for House members, statewide for senators, with the national figure for reference. The larger of the two values is highlighted.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500 animate-pulse" data-testid="constituency-compare-loading">Loading Census data…</p>
      ) : !da?.available && !db?.available ? (
        <p className="text-sm text-gray-500" data-testid="constituency-compare-unavailable">Census data is not available for these constituencies.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="constituency-compare-table">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left p-3 font-semibold text-gray-700">Metric</th>
                <th className="text-right p-3 font-semibold text-gray-900" data-testid="constituency-compare-head-a">
                  {short(a)}<span className="block text-[11px] font-normal text-gray-500">{da?.area?.name || "No data"}</span>
                </th>
                <th className="text-right p-3 font-semibold text-gray-900" data-testid="constituency-compare-head-b">
                  {short(b)}<span className="block text-[11px] font-normal text-gray-500">{db?.area?.name || "No data"}</span>
                </th>
                <th className="text-right p-3 font-semibold text-gray-500">U.S.</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => {
                const va = da?.area?.[r.key];
                const vb = db?.area?.[r.key];
                if (va == null && vb == null) return null;
                return (
                  <tr key={r.key} className="border-b border-gray-100" data-testid={`constituency-compare-row-${r.key}`}>
                    <td className="p-3 text-gray-700">{r.label}</td>
                    {cell(va, vb, r.fmt)}
                    {cell(vb, va, r.fmt)}
                    <td className="p-3 text-right font-mono tabular-nums text-gray-500">{national?.[r.key] == null ? ", " : r.fmt(national[r.key])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[11px] text-gray-400 mt-3" data-testid="constituency-compare-source">Source: U.S. Census Bureau, ACS 2020-2024 five-year estimates.</p>
        </div>
      )}
    </Card>
  );
};
